<script setup>
/**
 * Sign only header.
 *
 * @author Documill 2024
 */
import $ from 'jquery';
import { DocumillDropdownItem, DocumillDropdownMenu, DocumillIconButton, DocumillButton, DocumillProfile } from 'documill-ui-library';
import { useHeaderLogoUrl } from '../../utils/brand';
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const getUserName = computed(()=> {
  return store.getters.getUserFullName;
});

const getUserEmail = computed(()=> {
  return store.getters.getUserEmail;
});

const getUserOrganizationName = computed(()=> {
  return store.getters.getUserOrganizationName;
});

const getUserRole = computed(()=> {
  if(isGuest)
    return t("collaborators-role.sign-only");
  return t("users-role.sign-only");
});

const hasUserAvatar = computed(() => store.getters.hasUserAvatar);

function getUserInitials() {
  return getUserName.value.charAt(0).toUpperCase();
}

const imageURL = computed(() => {
  return store.getters.getAvatarURL;
});

const isGuest = computed(() => {
  return store.getters.getUserId == "guest";
});

const isHome = computed (() => {
  return router.currentRoute.value.name == "home";
});

/**
 * Whether the viewport is less than 480px wide.
 */
const isMobileView = computed (() => {
  return store.getters.isMobile;
});

const headerLogoUrl = useHeaderLogoUrl();

function openURL(url) {
  window.open(url,'_blank');
}

function dropdownOpen() {
  $(".user-name").addClass("user-name-open");
}

function dropdownClose() {
  $(".user-name").removeClass("user-name-open");
}

function logout() {
  store.dispatch("authLogout").then(() => {
    let loginUrl = window.FlowSignOnlyWebsiteGlobalConfig.basePaths.flowWebsite +"/login";
    window.location.href = loginUrl;
  });
}

function goProfilePage() {
  // Note: Set the organizationId since the URL path might not include it.
  router.push({
    'name': 'profile',
    'params': { 'organizationId': store.getters.getUserOrganizationId }
  });
}

function goHome() {
  router.push({
    'name': 'home',
    'params': { 'organizationId': store.getters.getUserOrganizationId }
  });
}

</script>

<template>
  <div class="header brand-header">
    <DocumillButton
      type="link"
      class="documill-logo-button"
      v-on:click="goHome()"
      :disabled="isGuest || isHome">
      <img :src="headerLogoUrl">
    </DocumillButton>
    <span class="dropdown-header">
      <DocumillDropdownMenu position="bottom-right">
        <template #trigger>
          <DocumillIconButton iconName="help-circle" />
        </template>
        <DocumillDropdownItem v-on:click="openURL('https://support.documill.com/leap/index.html')" isDivider > {{ $t("home.header.help-center.documill-help-center") }} </DocumillDropdownItem>
        <DocumillDropdownItem v-on:click="openURL('https://www.documill.com/terms-conditions')"> {{ $t("home.header.help-center.terms-of-service") }} </DocumillDropdownItem>
        <DocumillDropdownItem v-on:click="openURL('https://www.documill.com/privacy-policy/gdpr-compliance-statement/')"> {{ $t("home.header.help-center.gdpr") }} </DocumillDropdownItem>
        <DocumillDropdownItem v-on:click="openURL('https://www.documill.com/license-and-patent-notices/')"> {{ $t("home.header.help-center.license-patent") }} </DocumillDropdownItem>
      </DocumillDropdownMenu>

      <DocumillDropdownMenu position="bottom-right" @open="dropdownOpen" @close="dropdownClose">
        <template #trigger>
          <DocumillProfile
            class ="documill-profile"
            :view="isMobileView?'compact':'full'"
            :userImageUrl="hasUserAvatar? imageURL:null"
            :userName="getUserName"
            :userInitials="getUserInitials()">
          </DocumillProfile>
        </template>
        <DocumillDropdownItem class="item">
          <span class="wrapper-user">
            <span class="wrapper-name">
              <span class="text-m-bold text-primary name"> {{ getUserName }} </span>
              <span class="text-s-regular text-secondary"> {{ getUserEmail }} </span>
            </span>
            <span class="user-role">
              <div class="text-s-regular" v-html="$t('home.header.profile.role', {Role: getUserRole, Company: getUserOrganizationName})"></div>
            </span>
          </span>
        </DocumillDropdownItem>
        <DocumillDropdownItem v-if="isGuest == false" v-on:click="goProfilePage()"> {{ $t("home.header.profile.settings") }} </DocumillDropdownItem>
        <DocumillDropdownItem v-on:click="logout()"> {{ $t("home.header.profile.log-out") }} </DocumillDropdownItem>
      </DocumillDropdownMenu>
    </span>
  </div>
</template>

<style scoped>
.header {
  display: flex;
  background: var(--color-header-background);
  box-sizing: border-box;
  height: 50px;
  padding: 0 30px;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--color-divider);
}

.dropdown-header {
  align-items: center;
  display: flex;
  gap: 24px;
}

.profile {
  display: flex;
  gap: var(--space-100);
  align-items: center;
  color: var(--color-nav-default);
}

.text-primary {
  color: var(--color-text-primary);
}
.text-secondary {
  color: var(--color-text-secondary);
}
.user-role {
  display: flex;
  padding: 4px 8px;
  align-items: center;
  border-radius: 4px;
  background: var(--color-fill-accent);
  color: var(--color-text-accent);
}

.wrapper-user {
  display: grid;
  gap: 8px;
  cursor: default;
}

.wrapper-name {
  display: grid;
}

.item {
  background: #fff !important;
}

.name{
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.documill-profile {
  max-width: 70dvw;
}

.user-name:hover {
  color: var(--color-nav-hover);
}

.user-name-open {
  color: var(--color-nav-active) !important;
}

.documill-logo-button {
  margin-left:-8px;
}
</style>
